import { useEffect, useRef, useState } from 'react';

/** Quem pediu menos movimento no sistema não recebe animação nenhuma. */
export function prefereMenosMovimento(): boolean {
  return (
    typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

interface Opcoes {
  /** Fração do elemento que precisa aparecer para disparar. */
  readonly limiar?: number;
  /** Margem aplicada à área de observação, como no protótipo. */
  readonly margem?: string;
}

/**
 * Revela o conteúdo quando ele entra na tela, uma vez só.
 *
 * É o `.reveal` do protótipo (`js/main.js`), que ali era um observador global
 * varrendo o documento. Aqui vira um observador por seção: a seção sabe quando
 * apareceu e distribui isso aos filhos por classe, em vez de o componente
 * procurar elementos pelo DOM — que é o que quebraria assim que o React
 * remontasse a árvore.
 *
 * Já nasce revelado para quem pediu menos movimento, ou para navegador sem
 * `IntersectionObserver`: nunca esconder conteúdo por falta de suporte.
 */
export function useRevelarAoRolar<T extends Element>({
  limiar = 0.12,
  margem = '0px 0px -8% 0px',
}: Opcoes = {}) {
  const ref = useRef<T>(null);
  const [visivel, setVisivel] = useState(
    () => prefereMenosMovimento() || typeof IntersectionObserver === 'undefined',
  );

  useEffect(() => {
    if (visivel) return;

    const elemento = ref.current;

    if (!elemento) return;

    const observador = new IntersectionObserver(
      (entradas) => {
        if (entradas.some((entrada) => entrada.isIntersecting)) {
          setVisivel(true);
          observador.disconnect();
        }
      },
      { threshold: limiar, rootMargin: margem },
    );

    observador.observe(elemento);

    return () => observador.disconnect();
  }, [visivel, limiar, margem]);

  return { ref, visivel };
}
